import { isDefined } from '@sgme/fp';
import { Icon } from '@sgme/ui';
import type { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import { SgTooltip } from '@/components/sg-design-system/components/feedback/SgTooltip';
import { useUserContext } from '@/context/UserContext.tsx';

type Props = PropsWithChildren<{ placement: 'left' | 'right'; isNegativePremium?: boolean }>;

export const ForbiddenExecutionTooltip = ({ children, placement, isNegativePremium }: Props) => {
  const userContextValue = useUserContext();
  const {
    currentUser: { userType },
  } = userContextValue;

  const isInternalSales = userType === 'Internal Sales';
  // explicit reason why trading is forbidden (if forbidden)
  const tradePermission = isDefined(userContextValue)
    ? userContextValue.tradePermission
    : 'disallowedByUserPermissions'; // safe default until userContext is ready;

  if (tradePermission === 'allowed' && !isNegativePremium) {
    return children;
  }

  const reasonMessageId =
    tradePermission === 'disallowedByUserPermissions'
      ? 'cantTrade.tooltip.userCantTrade'
      : 'cantTrade.tooltip.lockedWorkspace';

  const premiumMessageId = isInternalSales
    ? 'request.PRIME_DISALLOWED.SALES.price'
    : 'request.PRIME_DISALLOWED.TRADERS.price';

  const tooltipMessageId = isNegativePremium ? premiumMessageId : reasonMessageId;

  return (
    <SgTooltip mode="hover" backgroundColor="black" color="white" placement={placement} arrow>
      <SgTooltip.Trigger>{children}</SgTooltip.Trigger>
      <SgTooltip.Content className="p-2 text-center">
        <FormattedMessage id="cantTrade.tooltip.tradingDisabled" />
        <br />
        <FormattedMessage
          id={tooltipMessageId}
          values={{ lockIcon: <Icon icon="lock" size="sm" className="text-danger" /> }}
        />
      </SgTooltip.Content>
    </SgTooltip>
  );
};
